
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { LazyRoute } from './components/LazyRoute';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { FreeWorldGuard } from './components/system/FreeWorldGuard';
import { AuthenticatedAppWrapper } from './components/auth/AuthenticatedAppWrapper';
import { TeacherSessionRedirect } from './components/auth/TeacherSessionRedirect';

// Lazy load pages with retry to avoid chunk errors after deploys
const Homepage = lazyWithRetry(() => import('./pages/Homepage'));
const About = lazyWithRetry(() => import('./pages/About'));
const Contact = lazyWithRetry(() => import('./pages/Contact'));
const Partners = lazyWithRetry(() => import('./pages/Partners'));
const AppEntry = lazyWithRetry(() => import('./pages/AppEntry'));
const Auth = lazyWithRetry(() => import('./pages/Auth'));
const AuthCallback = lazyWithRetry(() => import('./pages/AuthCallback'));
const ConfirmEmail = lazyWithRetry(() => import('./pages/ConfirmEmail'));
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'));
const AcceptInvite = lazyWithRetry(() => import('./pages/AcceptInvite'));
const ActivateLicense = lazyWithRetry(() => import('./pages/ActivateLicense'));
const JoinSchool = lazyWithRetry(() => import('./pages/JoinSchool'));
const Login = lazyWithRetry(() => import('./pages/Login'));
const StudentDashboard = lazyWithRetry(() => import('./pages/StudentDashboard'));
const TeacherDashboard = lazyWithRetry(() => import('./pages/TeacherDashboard'));
const AdminDashboard = lazyWithRetry(() => import('./pages/AdminDashboard'));
const FreeWorldMap = lazyWithRetry(() => import('./pages/FreeWorldMap'));
const LoppuhaastePage = lazyWithRetry(() => import('./pages/LoppuhaastePage'));
const ModulePage = lazyWithRetry(() => import('./pages/ModulePage'));
const ModuleTestPage = lazyWithRetry(() => import('./pages/ModuleTestPage'));
const NotFound = lazyWithRetry(() => import('./pages/NotFound'));
const AloitusPage = lazyWithRetry(() => import('./pages/AloitusPage'));
const TuloksetPage = lazyWithRetry(() => import('./pages/TuloksetPage'));
const Tiimista = lazyWithRetry(() => import('./pages/Tiimista'));
const Pelaa = lazyWithRetry(() => import('./pages/Pelaa'));
const ProtectedRoute = lazyWithRetry(() => import('./components/ProtectedRoute'));
const PlayerProtectedRoute = lazyWithRetry(() => import('./components/PlayerProtectedRoute'));
const RequireAdminRole = lazyWithRetry(() => import('./components/auth/RequireAdminRole'));
const TeacherSchoolGuard = lazyWithRetry(() => import('./components/auth/TeacherSchoolGuard'));
const AvatarOnboarding = lazyWithRetry(() => import('./pages/onboarding/AvatarOnboarding'));
const TeacherAvatarOnboarding = lazyWithRetry(() => import('./pages/onboarding/TeacherAvatarOnboarding'));
const PrintClass = lazyWithRetry(() => import('./pages/PrintClass'));
const MaintenancePage = lazyWithRetry(() => import('./pages/MaintenancePage'));
const SmokePage = lazyWithRetry(() => import('./pages/dev/SmokePage'));
const Demo = lazyWithRetry(() => import('./pages/Demo'));
const DemoChoice = lazyWithRetry(() => import('./pages/DemoChoice'));
const DemoTeacherDashboard = lazyWithRetry(() => import('./pages/DemoTeacherDashboard'));
const DemoProtectedRoute = lazyWithRetry(() => import('./components/auth/DemoProtectedRoute'));
const DemoLayout = lazyWithRetry(() => import('./components/system/DemoLayout'));
const ClassroomDemoEntry = lazyWithRetry(() => import('./pages/classroom/ClassroomDemoEntry'));
const ClassroomDemoFeedback = lazyWithRetry(() => import('./pages/classroom/ClassroomDemoFeedback'));
const ClassroomDemoSummary = lazyWithRetry(() => import('./pages/classroom/ClassroomDemoSummary'));
// NOTE: Demo student view reuses the REAL StudentDashboard — the demo player
// session token is persisted to localStorage so the regular flow renders.
// Demo teacher view uses a dedicated lightweight component (no Auth required).

/**
 * ROUTER CONFIGURATION
 * 
 * Routes are divided into:
 * 1. PUBLIC ROUTES - No session bootstrap, no player data fetching
 *    (/, /about, /contact, /partners, /tiimista, /pelaa, /login, /auth, /maintenance)
 * 
 * 2. AUTHENTICATED ROUTES - Wrapped in AuthenticatedAppWrapper
 *    These trigger session bootstrap and load player/module data
 *    (/student, /teacher, /admin, /freeworld, /module/*, /onboarding/*)
 */

export const router = createBrowserRouter([
  // ===========================================
  // PUBLIC ROUTES - No session bootstrap
  // ===========================================
  {
    path: "/dev/smoke",
    element: <LazyRoute component={SmokePage} routeName="Smoke Tests" />,
  },
  {
    path: "/maintenance",
    element: <LazyRoute component={MaintenancePage} routeName="Maintenance" />,
  },
  {
    path: "/",
    element: <LazyRoute component={Homepage} routeName="Homepage" />,
  },
  {
    path: "/about",
    element: <LazyRoute component={About} routeName="About" />,
  },
  {
    path: "/contact", 
    element: <LazyRoute component={Contact} routeName="Contact" />,
  },
  {
    path: "/partners",
    element: <LazyRoute component={Partners} routeName="Partners" />,
  },
  {
    path: "/tiimista",
    element: <LazyRoute component={Tiimista} routeName="Tiimista" />,
  },
  {
    path: "/pelaa",
    element: (
      <TeacherSessionRedirect>
        <LazyRoute component={Pelaa} routeName="Student Login" />
      </TeacherSessionRedirect>
    ),
  },
  {
    path: "/login",
    element: <LazyRoute component={Login} routeName="Login" />,
  },
  {
    path: "/auth",
    element: <LazyRoute component={Auth} routeName="Authentication" />,
  },
  {
    path: "/auth/callback",
    element: <LazyRoute component={AuthCallback} routeName="Auth Callback" />,
  },
  {
    path: "/auth/confirm-email",
    element: <LazyRoute component={ConfirmEmail} routeName="Confirm Email" />,
  },
  {
    path: "/auth/reset-password",
    element: <LazyRoute component={ResetPassword} routeName="Reset Password" />,
  },
  {
    path: "/accept-invite",
    element: <LazyRoute component={AcceptInvite} routeName="Accept Invite" />,
  },
  {
    path: "/activate-license",
    element: <LazyRoute component={ActivateLicense} routeName="Activate License" />,
  },
  {
    path: "/join-school",
    element: <LazyRoute component={JoinSchool} routeName="Join School" />,
  },
  {
    path: "/app",
    element: <LazyRoute component={AppEntry} routeName="App" />,
  },
  // ===========================================
  // DEMO MODE ROUTES
  // ===========================================
  {
    path: "/demo",
    element: <LazyRoute component={Demo} routeName="Demo" />,
  },
  {
    path: "/demo/choice",
    element: (
      <LazyRoute
        component={(props: any) => (
          <DemoProtectedRoute>
            <DemoChoice {...props} />
          </DemoProtectedRoute>
        )}
        routeName="Demo Choice"
      />
    ),
  },
  {
    path: "/demo/student",
    element: (
      <LazyRoute
        component={(props: any) => (
          <DemoProtectedRoute requireView="student">
            <DemoLayout>
              <StudentDashboard {...props} />
            </DemoLayout>
          </DemoProtectedRoute>
        )}
        routeName="Demo Student"
      />
    ),
  },
  {
    path: "/demo/teacher",
    element: (
      <LazyRoute
        component={(props: any) => (
          <DemoProtectedRoute requireView="teacher">
            <DemoLayout>
              <DemoTeacherDashboard {...props} />
            </DemoLayout>
          </DemoProtectedRoute>
        )}
        routeName="Demo Teacher"
      />
    ),
  },
  // ===========================================
  // CLASSROOM PILOT DEMO (separate from /demo)
  // ===========================================
  {
    path: "/luokkatila-demo",
    element: <LazyRoute component={ClassroomDemoEntry} routeName="Classroom Demo Entry" />,
  },
  {
    path: "/luokkatila-demo/palaute",
    element: <LazyRoute component={ClassroomDemoFeedback} routeName="Classroom Demo Feedback" />,
  },
  {
    path: "/luokkatila-demo/summary",
    element: (
      <AuthenticatedAppWrapper>
        <LazyRoute
          component={(props: any) => (
            <RequireAdminRole>
              <ClassroomDemoSummary {...props} />
            </RequireAdminRole>
          )}
          routeName="Classroom Demo Summary"
        />
      </AuthenticatedAppWrapper>
    ),
  },
  // English aliases
  {
    path: "/classroom-demo",
    element: <Navigate to="/luokkatila-demo" replace />,
  },
  {
    path: "/classroom-demo/summary",
    element: <Navigate to="/luokkatila-demo/summary" replace />,
  },

  // Print class requires teacher auth (moved to authenticated section below)
  
  // ===========================================
  // AUTHENTICATED ROUTES - With AuthenticatedAppWrapper
  // These trigger session bootstrap and load player data
  // ===========================================
  {
    path: "/student",
    element: (
      <TeacherSessionRedirect>
        <AuthenticatedAppWrapper>
          <LazyRoute 
            component={(props: any) => (
              <PlayerProtectedRoute>
                <StudentDashboard {...props} />
              </PlayerProtectedRoute>
            )} 
            routeName="Student Dashboard" 
          />
        </AuthenticatedAppWrapper>
      </TeacherSessionRedirect>
    ),
  },
  {
    path: "/opettaja",
    element: (
      <AuthenticatedAppWrapper>
        <LazyRoute 
          component={(props: any) => (
            <ProtectedRoute>
              <TeacherSchoolGuard>
                <TeacherDashboard {...props} />
              </TeacherSchoolGuard>
            </ProtectedRoute>
          )} 
          routeName="Teacher Dashboard" 
        />
      </AuthenticatedAppWrapper>
    ),
  },
  {
    // Backward-compat alias: redirect old /teacher URL to Finnish /opettaja
    path: "/teacher",
    element: <Navigate to="/opettaja" replace />,
  },
  {
    path: "/admin",
    element: (
      <AuthenticatedAppWrapper>
        <LazyRoute 
          component={(props: any) => (
            <RequireAdminRole>
              <AdminDashboard {...props} />
            </RequireAdminRole>
          )} 
          routeName="Admin Dashboard" 
        />
      </AuthenticatedAppWrapper>
    ),
  },
  {
    path: "/admin/*",
    element: (
      <AuthenticatedAppWrapper>
        <LazyRoute 
          component={(props: any) => (
            <RequireAdminRole>
              <AdminDashboard {...props} />
            </RequireAdminRole>
          )} 
          routeName="Admin Dashboard" 
        />
      </AuthenticatedAppWrapper>
    ),
  },
  // Print class - requires teacher auth but no player session
  {
    path: "/print-class/:groupId",
    element: (
      <AuthenticatedAppWrapper>
        <LazyRoute 
          component={(props: any) => (
            <ProtectedRoute>
              <PrintClass {...props} />
            </ProtectedRoute>
          )} 
          routeName="Print Class" 
        />
      </AuthenticatedAppWrapper>
    ),
  },
  {
    path: "/tulokset",
    element: (
      <AuthenticatedAppWrapper>
        <LazyRoute
          component={(props: any) => (
            <PlayerProtectedRoute>
              <TuloksetPage {...props} />
            </PlayerProtectedRoute>
          )}
          routeName="Tulokset"
        />
      </AuthenticatedAppWrapper>
    ),
  },
  {
    path: "/loppuhaaste",
    element: (
      <AuthenticatedAppWrapper>
        <LazyRoute
          component={(props: any) => (
            <PlayerProtectedRoute>
              <FreeWorldGuard>
                <LoppuhaastePage {...props} />
              </FreeWorldGuard>
            </PlayerProtectedRoute>
          )}
          routeName="Loppuhaaste"
        />
      </AuthenticatedAppWrapper>
    ),
  },
  {
    // English alias — same dedicated full-page challenge view
    path: "/final-challenge",
    element: <Navigate to="/loppuhaaste" replace />,
  },
  {
    // Product surface is Loppuhaaste; keep /freeworld as stable entry URL
    path: "/freeworld",
    element: <Navigate to="/loppuhaaste" replace />,
  },
  {
    // Full Free World map (not linked while Loppuhaaste is the player entry)
    path: "/freeworld/map",
    element: (
      <AuthenticatedAppWrapper>
        <LazyRoute 
          component={(props: any) => (
            <PlayerProtectedRoute>
              <FreeWorldGuard>
                <FreeWorldMap {...props} />
              </FreeWorldGuard>
            </PlayerProtectedRoute>
          )} 
          routeName="Free World Map" 
        />
      </AuthenticatedAppWrapper>
    ),
  },
  {
    path: "/module/:moduleId",
    element: (
      <AuthenticatedAppWrapper>
        <LazyRoute 
          component={(props: any) => (
            <PlayerProtectedRoute>
              <ModulePage {...props} />
            </PlayerProtectedRoute>
          )} 
          routeName="Module" 
        />
      </AuthenticatedAppWrapper>
    ),
  },
  {
    path: "/module/:moduleId/:testType",
    element: (
      <AuthenticatedAppWrapper>
        <LazyRoute 
          component={(props: any) => (
            <PlayerProtectedRoute>
              <ModuleTestPage {...props} />
            </PlayerProtectedRoute>
          )} 
          routeName="Module Test" 
        />
      </AuthenticatedAppWrapper>
    ),
  },
  {
    path: "/aloitus",
    element: (
      <AuthenticatedAppWrapper>
        <LazyRoute 
          component={(props: any) => (
            <ProtectedRoute>
              <AloitusPage {...props} />
            </ProtectedRoute>
          )} 
          routeName="Aloitus" 
        />
      </AuthenticatedAppWrapper>
    ),
  },
  {
    path: "/aloitus/:sectionKey",
    element: (
      <AuthenticatedAppWrapper>
        <LazyRoute 
          component={(props: any) => (
            <PlayerProtectedRoute>
              <AloitusPage {...props} />
            </PlayerProtectedRoute>
          )} 
          routeName="Aloitus" 
        />
      </AuthenticatedAppWrapper>
    ),
  },
  {
    path: "/onboarding/avatar",
    element: (
      <TeacherSessionRedirect>
        <AuthenticatedAppWrapper>
          <LazyRoute 
            component={(props: any) => (
              <PlayerProtectedRoute requireOnboarding={false}>
                <AvatarOnboarding {...props} />
              </PlayerProtectedRoute>
            )} 
            routeName="Avatar Onboarding" 
          />
        </AuthenticatedAppWrapper>
      </TeacherSessionRedirect>
    ),
  },
  {
    path: "/onboarding/teacher-avatar",
    element: (
      <AuthenticatedAppWrapper>
        <LazyRoute 
          component={(props: any) => (
            <PlayerProtectedRoute requireOnboarding={false}>
              <TeacherAvatarOnboarding {...props} />
            </PlayerProtectedRoute>
          )} 
          routeName="Teacher Avatar Onboarding" 
        />
      </AuthenticatedAppWrapper>
    ),
  },
  
  // ===========================================
  // FALLBACK
  // ===========================================
  {
    path: "*",
    element: <LazyRoute component={NotFound} routeName="404" />,
  },
]);
